import '../styles/custom.css'

import {
  EuiPageBody,
  EuiPageContent,
  EuiPageContentHeader,
  EuiPageContentHeaderSection,
  EuiPageContentBody,
  EuiTitle,
  EuiText,
  EuiSpacer,
  EuiAccordion,
  EuiIcon,
  EuiFlexGroup,
  EuiFlexItem,
  EuiCard,
  EuiCallOut,
  EuiListGroup,
  EuiListGroupItem,
  EuiHorizontalRule,
  EuiPanel,
  EuiBadge,
  EuiButton,
  EuiImage,
  EuiStat,
  EuiProgress,
  EuiTable,
  EuiTableHeader,
  EuiTableHeaderCell,
  EuiTableBody,
  EuiTableRow,
  EuiTableRowCell,
  EuiCodeBlock,
  EuiSteps,
  EuiMarkdownFormat,
} from '@elastic/eui'

import PageHeader from '../components/Header'

function Help() {
  const quickStartSteps = [
    {
      title: 'Connexion',
      children: 'Connectez-vous avec vos identifiants CyberVision',
      status: 'complete',
    },
    {
      title: 'Dashboard',
      children: 'Consultez les métriques de sécurité en temps réel',
      status: 'complete',
    },
    {
      title: 'Configuration',
      children: 'Configurez vos KPIs et alertes personnalisées',
      status: 'incomplete',
    },
    {
      title: 'Surveillance',
      children: 'Surveillez et analysez les menaces détectées',
      status: 'incomplete',
    },
  ]

  const features = [
    {
      title: 'Surveillance en temps réel',
      description: 'Monitoring continu de votre infrastructure de sécurité',
      icon: 'securitySignal',
      color: 'primary',
      details: [
        'Surveillance des équipements réseau (ER)',
        'Détection automatique des anomalies',
        'Alertes en temps réel',
        'Tableau de bord personnalisable'
      ]
    },
    {
      title: 'Analyse MITRE ATT&CK',
      description: 'Classification des techniques d\'attaque selon les standards',
      icon: 'securityAnalytics',
      color: 'success',
      details: [
        'Framework MITRE ATT&CK intégré',
        'Classification automatique des menaces',
        'Cartographie des techniques d\'attaque',
        'Rapports de conformité'
      ]
    },
    {
      title: 'Gestion des KPIs',
      description: 'Indicateurs de performance personnalisables',
      icon: 'stats',
      color: 'warning',
      details: [
        'KPIs configurables par utilisateur',
        'Historique des métriques',
        'Rapports automatisés',
        'Export des données'
      ]
    },
    {
      title: 'Administration avancée',
      description: 'Gestion complète de la plateforme',
      icon: 'gear',
      color: 'danger',
      details: [
        'Gestion des utilisateurs et rôles',
        'Configuration système',
        'Logs d\'audit et de connexion',
        'Sauvegarde et restauration'
      ]
    }
  ]

  const kpiTypes = [
    { name: 'ER Supervisés', type: 'doughnut', description: 'Surveillance des équipements réseau', color: '#0079A5' },
    { name: 'Mitre Attack', type: 'radar', description: 'Analyse des techniques d\'attaque', color: '#DD0A73' },
    { name: 'Périmètres supervisés', type: 'bar', description: 'Monitoring des zones de sécurité', color: '#00A69B' },
    { name: 'Volume de logs', type: 'line', description: 'Analyse du trafic journalier', color: '#F5A700' }
  ]

  return (
    <>
      <EuiPageBody component="div">
        <PageHeader />
        <EuiPageContent id="contentPage">
          <EuiPageContentHeader>
            <EuiPageContentHeaderSection>
              <EuiTitle size="l">
                <h1>
                  <EuiIcon type="help" size="l" style={{ marginRight: '12px' }} />
                  Centre d'aide CyberVision
                </h1>
              </EuiTitle>
            </EuiPageContentHeaderSection>
          </EuiPageContentHeader>
          <EuiPageContentBody>
            
            {/* Introduction */}
            <EuiCallOut
              title="Bienvenue dans CyberVision SOC"
              iconType="securitySignal"
              color="primary"
              size="l"
            >
              <p>
                <strong>CyberVision</strong> est votre plateforme de supervision de la cybersécurité (SOC) 
                qui vous permet de surveiller, analyser et répondre aux menaces de sécurité en temps réel. 
                Cette documentation complète vous accompagne dans l'utilisation de toutes les fonctionnalités.
              </p>
            </EuiCallOut>

            <EuiSpacer size="xl" />

            {/* Quick Start */}
            <EuiPanel paddingSize="l" hasShadow>
              <EuiTitle size="m">
                <h2><EuiIcon type="play" size="m" style={{ marginRight: '8px' }} />Démarrage rapide</h2>
              </EuiTitle>
              <EuiSpacer size="m" />
              <EuiSteps steps={quickStartSteps} />
            </EuiPanel>

            <EuiSpacer size="xl" />

            {/* Features Overview */}
            <EuiTitle size="m">
              <h2><EuiIcon type="apps" size="m" style={{ marginRight: '8px' }} />Fonctionnalités principales</h2>
            </EuiTitle>
            <EuiSpacer size="l" />
            
            <EuiFlexGroup gutterSize="l">
              {features.map((feature, index) => (
                <EuiFlexItem key={index}>
                  <EuiCard
                    title={feature.title}
                    description={feature.description}
                    icon={<EuiIcon type={feature.icon} size="xl" color={feature.color} />}
                    footer={
                      <div>
                        <EuiText size="s">
                          <ul>
                            {feature.details.map((detail, i) => (
                              <li key={i}>{detail}</li>
                            ))}
                          </ul>
                        </EuiText>
                      </div>
                    }
                  />
                </EuiFlexItem>
              ))}
            </EuiFlexGroup>

            <EuiSpacer size="xl" />

            {/* Dashboard Section */}
            <EuiPanel paddingSize="l" hasShadow>
              <EuiTitle size="m">
                <h2><EuiIcon type="dashboardApp" size="m" style={{ marginRight: '8px' }} />Tableau de bord</h2>
              </EuiTitle>
              <EuiSpacer size="m" />
              
              <EuiText>
                <p>
                  Le tableau de bord centralise toutes les métriques de sécurité en temps réel. 
                  Vous y trouverez les graphiques et indicateurs essentiels pour la surveillance de votre infrastructure.
                </p>
              </EuiText>

              <EuiSpacer size="l" />

              <EuiTitle size="s">
                <h3>Types de graphiques disponibles</h3>
              </EuiTitle>
              <EuiSpacer size="m" />

              <EuiFlexGroup gutterSize="m">
                {kpiTypes.map((kpi, index) => (
                  <EuiFlexItem key={index}>
                    <EuiCard
                      title={kpi.name}
                      description={kpi.description}
                      icon={<EuiIcon type="chart" size="l" color={kpi.color} />}
                      footer={
                        <EuiBadge color="primary">{kpi.type}</EuiBadge>
                      }
                    />
                  </EuiFlexItem>
                ))}
              </EuiFlexGroup>
            </EuiPanel>

            <EuiSpacer size="xl" />

            {/* Detailed Sections */}
            <EuiAccordion
              id="dashboard-details"
              buttonContent={
                <span>
                  <EuiIcon type="dashboardApp" style={{ marginRight: '8px' }} />
                  Guide détaillé du tableau de bord
                </span>
              }
              paddingSize="l"
            >
              <EuiText>
                <h4>Navigation dans le dashboard</h4>
                <p>
                  Le tableau de bord est organisé en sections clairement identifiées :
                </p>
                <ul>
                  <li><strong>Métriques en temps réel</strong> : Affichage des KPIs principaux</li>
                  <li><strong>Graphiques interactifs</strong> : Zoom, filtres temporels, export</li>
                  <li><strong>Alertes actives</strong> : Notifications des menaces détectées</li>
                  <li><strong>Statistiques</strong> : Tendances et analyses historiques</li>
                </ul>
                
                <EuiSpacer size="m" />
                
                <h4>Personnalisation</h4>
                <p>
                  Vous pouvez personnaliser votre tableau de bord selon vos besoins :
                </p>
                <ul>
                  <li>Réorganiser les widgets par glisser-déposer</li>
                  <li>Modifier les périodes d'analyse</li>
                  <li>Configurer les seuils d'alerte</li>
                  <li>Exporter les données en différents formats</li>
                </ul>
              </EuiText>
            </EuiAccordion>

            <EuiSpacer size="m" />

            <EuiAccordion
              id="kpis-management"
              buttonContent={
                <span>
                  <EuiIcon type="stats" style={{ marginRight: '8px' }} />
                  Gestion des KPIs
                </span>
              }
              paddingSize="l"
            >
              <EuiText>
                <h4>Configuration des indicateurs de performance</h4>
                <p>
                  Les KPIs (Key Performance Indicators) sont au cœur de la surveillance de votre infrastructure. 
                  Ils permettent de mesurer l'efficacité de vos mesures de sécurité.
                </p>
                
                <EuiSpacer size="m" />
                
                <h5>Types de KPIs disponibles :</h5>
                <EuiListGroup>
                  <EuiListGroupItem
                    label="ER Supervisés - Surveillance des équipements réseau"
                    iconType="networkDrive"
                    extraAction={{
                      color: 'primary',
                      iconType: 'check',
                      alwaysShow: true,
                    }}
                  />
                  <EuiListGroupItem
                    label="Mitre Attack - Analyse des techniques d'attaque"
                    iconType="securitySignal"
                    extraAction={{
                      color: 'primary',
                      iconType: 'check',
                      alwaysShow: true,
                    }}
                  />
                  <EuiListGroupItem
                    label="Périmètres supervisés - Monitoring des zones de sécurité"
                    iconType="securityAnalytics"
                    extraAction={{
                      color: 'primary',
                      iconType: 'check',
                      alwaysShow: true,
                    }}
                  />
                  <EuiListGroupItem
                    label="Volume de logs - Analyse du trafic journalier"
                    iconType="logsApp"
                    extraAction={{
                      color: 'primary',
                      iconType: 'check',
                      alwaysShow: true,
                    }}
                  />
                </EuiListGroup>

                <EuiSpacer size="m" />
                
                <h5>Configuration avancée :</h5>
                <ul>
                  <li><strong>Seuils personnalisés</strong> : Définissez vos propres seuils d'alerte</li>
                  <li><strong>Périodes d'analyse</strong> : Configurez les intervalles de calcul</li>
                  <li><strong>Filtres</strong> : Appliquez des filtres par type d'équipement ou zone</li>
                  <li><strong>Notifications</strong> : Configurez les alertes par email ou SMS</li>
                </ul>
              </EuiText>
            </EuiAccordion>

            <EuiSpacer size="m" />

            <EuiAccordion
              id="administration-guide"
              buttonContent={
                <span>
                  <EuiIcon type="gear" style={{ marginRight: '8px' }} />
                  Guide d'administration
                </span>
              }
              paddingSize="l"
            >
              <EuiText>
                <h4>Interface d'administration</h4>
                <p>
                  L'interface d'administration vous donne un contrôle total sur la plateforme CyberVision. 
                  Accédez-y via le menu principal ou l'icône d'administration.
                </p>
                
                <EuiSpacer size="m" />
                
                <h5>Fonctionnalités administratives :</h5>
                
                <EuiPanel color="subdued" paddingSize="m">
                  <h6>📊 Rapports</h6>
                  <p>Génération et consultation des rapports de sécurité :</p>
                  <ul>
                    <li>Rapports de conformité</li>
                    <li>Analyses de menaces</li>
                    <li>Statistiques d'utilisation</li>
                    <li>Export en PDF, Excel, CSV</li>
                  </ul>
                </EuiPanel>

                <EuiSpacer size="s" />

                <EuiPanel color="subdued" paddingSize="m">
                  <h6>👥 Connexions</h6>
                  <p>Gestion des utilisateurs et sessions :</p>
                  <ul>
                    <li>Suivi des connexions actives</li>
                    <li>Historique des sessions</li>
                    <li>Gestion des permissions</li>
                    <li>Audit des accès</li>
                  </ul>
                </EuiPanel>

                <EuiSpacer size="s" />

                <EuiPanel color="subdued" paddingSize="m">
                  <h6>📈 Agrégations</h6>
                  <p>Historique des calculs et données :</p>
                  <ul>
                    <li>Logs d'agrégation des KPIs</li>
                    <li>Historique des calculs</li>
                    <li>Statistiques de performance</li>
                    <li>Diagnostics système</li>
                  </ul>
                </EuiPanel>

                <EuiSpacer size="s" />

                <EuiPanel color="subdued" paddingSize="m">
                  <h6>⚙️ Paramètres</h6>
                  <p>Configuration système avancée :</p>
                  <ul>
                    <li>Configuration des API</li>
                    <li>Paramètres de sécurité</li>
                    <li>Gestion des thèmes</li>
                    <li>Sauvegarde et restauration</li>
                  </ul>
                </EuiPanel>
              </EuiText>
            </EuiAccordion>

            <EuiSpacer size="m" />

            <EuiAccordion
              id="security-features"
              buttonContent={
                <span>
                  <EuiIcon type="securitySignal" style={{ marginRight: '8px' }} />
                  Fonctionnalités de sécurité
                </span>
              }
              paddingSize="l"
            >
              <EuiText>
                <h4>Surveillance et détection avancées</h4>
                
                <EuiCallOut
                  title="Framework MITRE ATT&CK"
                  iconType="securitySignal"
                  color="primary"
                >
                  <p>
                    CyberVision intègre le framework MITRE ATT&CK pour une classification 
                    professionnelle des techniques d'attaque et une meilleure compréhension des menaces.
                  </p>
                </EuiCallOut>

                <EuiSpacer size="m" />
                
                <h5>Capacités de détection :</h5>
                <EuiFlexGroup direction="column" gutterSize="m">
                  <EuiFlexItem>
                    <EuiPanel paddingSize="m" color="subdued">
                      <EuiFlexGroup alignItems="center" gutterSize="m">
                        <EuiFlexItem grow={false}>
                          <EuiIcon type="networkDrive" size="l" color="primary" />
                        </EuiFlexItem>
                        <EuiFlexItem>
                          <div>
                            <h6>Surveillance réseau en temps réel</h6>
                            <p>Monitoring continu des équipements et flux réseau avec détection automatique des anomalies.</p>
                          </div>
                        </EuiFlexItem>
                      </EuiFlexGroup>
                    </EuiPanel>
                  </EuiFlexItem>
                  
                  <EuiFlexItem>
                    <EuiPanel paddingSize="m" color="subdued">
                      <EuiFlexGroup alignItems="center" gutterSize="m">
                        <EuiFlexItem grow={false}>
                          <EuiIcon type="securityAnalytics" size="l" color="success" />
                        </EuiFlexItem>
                        <EuiFlexItem>
                          <div>
                            <h6>Analyse comportementale</h6>
                            <p>Détection des comportements suspects basée sur l'apprentissage automatique et les patterns d'attaque.</p>
                          </div>
                        </EuiFlexItem>
                      </EuiFlexGroup>
                    </EuiPanel>
                  </EuiFlexItem>
                  
                  <EuiFlexItem>
                    <EuiPanel paddingSize="m" color="subdued">
                      <EuiFlexGroup alignItems="center" gutterSize="m">
                        <EuiFlexItem grow={false}>
                          <EuiIcon type="link" size="l" color="warning" />
                        </EuiFlexItem>
                        <EuiFlexItem>
                          <div>
                            <h6>Corrélation d'événements</h6>
                            <p>Mise en relation des événements de sécurité pour identifier les campagnes d'attaque complexes.</p>
                          </div>
                        </EuiFlexItem>
                      </EuiFlexGroup>
                    </EuiPanel>
                  </EuiFlexItem>
                  
                  <EuiFlexItem>
                    <EuiPanel paddingSize="m" color="subdued">
                      <EuiFlexGroup alignItems="center" gutterSize="m">
                        <EuiFlexItem grow={false}>
                          <EuiIcon type="bell" size="l" color="danger" />
                        </EuiFlexItem>
                        <EuiFlexItem>
                          <div>
                            <h6>Alertes intelligentes</h6>
                            <p>Système d'alertes adaptatif qui réduit les faux positifs et priorise les menaces réelles.</p>
                          </div>
                        </EuiFlexItem>
                      </EuiFlexGroup>
                    </EuiPanel>
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiText>
            </EuiAccordion>

            <EuiSpacer size="m" />

            <EuiAccordion
              id="troubleshooting"
              buttonContent={
                <span>
                  <EuiIcon type="wrench" style={{ marginRight: '8px' }} />
                  Dépannage et support
                </span>
              }
              paddingSize="l"
            >
              <EuiText>
                <h4>Résolution des problèmes courants</h4>
                
                <EuiAccordion
                  id="login-problems"
                  buttonContent="🔐 Problèmes de connexion"
                >
                  <div style={{ padding: '16px' }}>
                    <h6>Symptômes :</h6>
                    <ul>
                      <li>Impossible de se connecter à l'application</li>
                      <li>Message d'erreur "Session expirée"</li>
                      <li>Redirection vers la page de connexion</li>
                    </ul>
                    
                    <h6>Solutions :</h6>
                    <ol>
                      <li>Vérifiez vos identifiants de connexion</li>
                      <li>Videz le cache de votre navigateur (Ctrl+Shift+Suppr)</li>
                      <li>Vérifiez que votre session n'a pas expiré</li>
                      <li>Contactez votre administrateur système</li>
                    </ol>
                  </div>
                </EuiAccordion>

                <EuiSpacer size="s" />

                <EuiAccordion
                  id="chart-problems"
                  buttonContent="📊 Problèmes d'affichage des graphiques"
                >
                  <div style={{ padding: '16px' }}>
                    <h6>Symptômes :</h6>
                    <ul>
                      <li>Graphiques ne se chargent pas</li>
                      <li>Données manquantes ou incorrectes</li>
                      <li>Erreurs JavaScript dans la console</li>
                    </ul>
                    
                    <h6>Solutions :</h6>
                    <ol>
                      <li>Actualisez la page (F5 ou Ctrl+R)</li>
                      <li>Vérifiez votre connexion internet</li>
                      <li>Videz le cache et les cookies</li>
                      <li>Vérifiez que JavaScript est activé</li>
                      <li>Testez avec un autre navigateur</li>
                    </ol>
                  </div>
                </EuiAccordion>

                <EuiSpacer size="s" />

                <EuiAccordion
                  id="data-problems"
                  buttonContent="📈 Problèmes de données"
                >
                  <div style={{ padding: '16px' }}>
                    <h6>Symptômes :</h6>
                    <ul>
                      <li>Données manquantes ou obsolètes</li>
                      <li>KPIs ne se mettent pas à jour</li>
                      <li>Erreurs dans les logs d'agrégation</li>
                    </ul>
                    
                    <h6>Solutions :</h6>
                    <ol>
                      <li>Vérifiez la configuration des KPIs</li>
                      <li>Consultez les logs d'agrégation dans l'administration</li>
                      <li>Vérifiez la connectivité avec les sources de données</li>
                      <li>Contactez l'équipe technique</li>
                    </ol>
                  </div>
                </EuiAccordion>
              </EuiText>
            </EuiAccordion>

            <EuiSpacer size="xl" />

            {/* Contact and Support */}
            <EuiPanel paddingSize="l" hasShadow color="primary">
              <EuiFlexGroup alignItems="center">
                <EuiFlexItem grow={false}>
                  <EuiIcon type="help" size="xxl" color="white" />
                </EuiFlexItem>
                <EuiFlexItem>
                  <EuiTitle size="m">
                    <h2 style={{ color: 'white' }}>Besoin d'aide supplémentaire ?</h2>
                  </EuiTitle>
                  <EuiText color="white">
                    <p>
                      Notre équipe de support technique est disponible pour vous accompagner 
                      dans l'utilisation de CyberVision. N'hésitez pas à nous contacter.
                    </p>
                  </EuiText>
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton fill color="white" iconType="email">
                    Contacter le support
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiPanel>

            <EuiSpacer size="l" />

            {/* Footer */}
            <EuiText textAlign="center" color="subdued">
              <p>
                <EuiIcon type="heart" color="danger" style={{ marginRight: '4px' }} />
                Développé avec passion par l'équipe <strong>Pic Digital</strong> • Version 1.0.0
              </p>
            </EuiText>

          </EuiPageContentBody>
        </EuiPageContent>
      </EuiPageBody>
    </>
  )
}

export default Help